import * as B from '@blueprintjs/core';
import * as I from 'immutable';
import React from 'react';

import {unreachableChatTools} from 'app/components/AgentChat/chatToolPermissions';
import {
  CHAT_TOOL_PERMISSIONS,
  CHAT_TOOL_PERMISSION_LABELS,
  ChatTool,
  ChatToolPermission,
  ChatToolPermissions,
  catalogByGroup,
  effectivePermission,
  permissionFor,
  permissionOptionsFor,
} from 'app/components/AgentChat/toolCatalog';
import {usePushNotification} from 'app/components/Notification';
import {ApiOrganization, ApiOrganizationUser} from 'app/modules/Remote/Organization';
import {LoggedInUserActions} from 'app/providers/AuthProvider';

import cs from './ChatToolsView.module.scss';
import * as GlobalSettings from './GlobalSettings';

const SUBTITLE =
  'Auto runs the tool without asking. Ask me shows a card in the chat you answer before every ' +
  'call. Denied means the agent cannot use the tool at all. These are your own choices, and they ' +
  'follow you into every organization you work in.';

/** The permission every tool in a section is on, or null when they differ. */
function sharedPermission(
  tools: ChatTool[],
  permissions: ChatToolPermissions
): ChatToolPermission | null {
  const [first, ...rest] = tools.map((tool) => effectivePermission(tool.name, permissions));
  return rest.every((permission) => permission === first) ? first : null;
}

function permissionOptions(tool: ChatTool): {label: string; value: ChatToolPermission}[] {
  return permissionOptionsFor(tool).map((permission) => ({
    label: CHAT_TOOL_PERMISSION_LABELS[permission],
    value: permission,
  }));
}

const ChatToolsView: React.FunctionComponent<
  React.PropsWithChildren<{
    profile: I.ImmutableOf<ApiOrganizationUser>;
    organization: I.ImmutableOf<ApiOrganization> | null;
    loggedInUserActions: LoggedInUserActions;
  }>
> = ({profile, organization, loggedInUserActions}) => {
  const pushNotification = usePushNotification();
  // The map being edited is held here rather than read back from the profile:
  // `updateSettings` writes the profile optimistically, so a failed save needs
  // somewhere to put the old choice back.
  const [permissions, setPermissions] = React.useState<ChatToolPermissions>(
    () => profile.getIn(['settings', 'chatToolPermissions'])?.toJS() ?? {}
  );
  const [isSaving, setIsSaving] = React.useState(false);

  const save = React.useCallback(
    async (next: ChatToolPermissions) => {
      const previous = permissions;
      setPermissions(next);
      setIsSaving(true);
      try {
        // The whole map every time: the backend merges only top-level settings keys.
        await loggedInUserActions.updateSettings({chatToolPermissions: next});
      } catch (_e) {
        setPermissions(previous);
        pushNotification({
          message: 'There was a problem saving that setting. Please try again.',
          autoHideDuration: 3000,
          options: {
            intent: B.Intent.DANGER,
          },
        });
      } finally {
        setIsSaving(false);
      }
    },
    [loggedInUserActions, permissions, pushNotification]
  );

  return (
    <GlobalSettings.Body
      title="Chat agent"
      subtitle={SUBTITLE}
      titleActions={
        <B.Button
          icon="reset"
          disabled={isSaving || Object.keys(permissions).length === 0}
          onClick={() => void save({})}
        >
          Reset to defaults
        </B.Button>
      }
    >
      {catalogByGroup(unreachableChatTools(profile, organization)).map(({group, tools}) => {
        const shared = sharedPermission(tools, permissions);
        return (
          <div className={cs.group} key={group}>
            <div className={cs.groupHeader}>
              <h1 className={cs.groupTitle}>{group}</h1>
              <div className={cs.setAll} role="group" aria-label={`Set all ${group}`}>
                <span className={cs.setAllLabel}>Set all to</span>
                <B.ButtonGroup>
                  {CHAT_TOOL_PERMISSIONS.map((permission) => (
                    <B.Button
                      key={permission}
                      small
                      active={shared === permission}
                      disabled={isSaving}
                      onClick={() =>
                        void save({
                          ...permissions,
                          ...Object.fromEntries(
                            tools.map((tool) => [tool.name, permissionFor(tool, permission)])
                          ),
                        })
                      }
                    >
                      {CHAT_TOOL_PERMISSION_LABELS[permission]}
                    </B.Button>
                  ))}
                </B.ButtonGroup>
              </div>
            </div>
            {tools.map((tool) => (
              <div className={cs.tool} key={tool.name}>
                <div className={cs.toolText}>
                  <div className={cs.toolLabel}>{tool.label}</div>
                  <div className={cs.toolDescription}>{tool.description}</div>
                </div>
                <div className={cs.toolControl} role="group" aria-label={tool.label}>
                  <B.SegmentedControl
                    small
                    options={permissionOptions(tool).map((option) => ({
                      ...option,
                      disabled: isSaving,
                    }))}
                    value={effectivePermission(tool.name, permissions)}
                    onValueChange={(value) =>
                      void save({...permissions, [tool.name]: value as ChatToolPermission})
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </GlobalSettings.Body>
  );
};

export default ChatToolsView;
